import * as React from 'react';
import { Flex } from '../../Flex';
import { Badge } from '@/hen-ui/atomics/Badge';
import { Button } from '@/hen-ui/atomics/Button';
import { Heading } from '@/hen-ui/atomics/Heading';
import { Text } from '@/hen-ui/atomics/Text';
import type {
  ComparisonColumn,
  ComparisonRowDef,
  ComparisonCtaLocation,
} from '../types';
import { isEmptyColumn } from '../utils/helpers';
import { useMobileTableScrollApi } from '../hooks/useMobileTableScrollApi';
import { ComparisonItemImage } from './ComparisonItemImage';
import { EmptySlotHeader } from './EmptySlotHeader';
import { CellContent } from './CellContent';
import { cn } from '@/hen-ui/utils/cn';
import { CarouselPrevious, CarouselNext, CarouselDots } from '../../Carousel';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/hen-ui/composites/Table';

interface ComparisonTableMobileProps {
  columns: ComparisonColumn[];
  rowDefs: ComparisonRowDef[];
  safeItems: ComparisonColumn[];
  styleVersion: 'v1' | 'v2';
  infiniteScroll?: boolean;
  inView?: boolean;
  onPageChange?: (page: number) => void;
  onProductClicked?: (id: string) => void;
  onCtaClick?: (
    id: string,
    cta: { label: string; href: string },
    location: ComparisonCtaLocation,
  ) => void;
}

export const ComparisonTableMobile: React.FC<ComparisonTableMobileProps> = ({
  columns,
  rowDefs,
  safeItems,
  styleVersion,
  infiniteScroll = false,
  inView: _inView = false,
  onPageChange,
  onProductClicked,
  onCtaClick,
}) => {
  const hasPinned = columns.length > 2;

  const {
    scrollContainerRef,
    headerScrollContainerRef,
    imageScrollContainerRef,
    colStyle,
    tableMinWidth,
    pinnedColWidth,
    canScrollPrev,
    canScrollNext,
    currentPage,
    totalPages,
    scrollPrev,
    scrollNext,
    scrollToPage,
  } = useMobileTableScrollApi({ columnCount: columns.length, infiniteScroll });

  React.useEffect(() => {
    onPageChange?.(currentPage);
  }, [currentPage, onPageChange]);

  const tableStyle = tableMinWidth ? { minWidth: tableMinWidth } : undefined;

  const pinnedColStyle: React.CSSProperties | undefined = pinnedColWidth
    ? { width: pinnedColWidth, minWidth: pinnedColWidth }
    : undefined;

  const colgroup = (
    <colgroup>
      {columns.map((col, i) => (
        <col
          key={col.id}
          style={i === 0 && hasPinned ? pinnedColStyle : colStyle}
        />
      ))}
    </colgroup>
  );

  return (
    <Flex className='md:hidden' direction='column' gap='sm'>
      {/* White card */}
      <Flex
        direction='column'
        gap='none'
        className={cn(
          'bg-white [overflow:clip] rounded-xl',
          styleVersion === 'v1' && 'p-4',
        )}
      >
        {/*
         * Image row — NOT sticky, scrolls away with the page.
         * Synced horizontally via imageScrollContainerRef.
         */}
        <Flex
          ref={imageScrollContainerRef}
          gap='none'
          className='overflow-x-hidden'
        >
          <Table
            className='table-fixed w-full border-separate border-spacing-0'
            noWrapper
            style={tableStyle}
          >
            {colgroup}
            <TableHeader>
              <TableRow className='border-none'>
                {columns.map((column, index) => (
                  <TableHead
                    key={`${column.id}-image`}
                    className={cn(
                      'pt-4 px-2 align-top font-normal',
                      hasPinned && index === 0 && 'sticky left-0 z-10 bg-white',
                    )}
                  >
                    <Flex direction='column' gap='xs'>
                      {index === 0 && !isEmptyColumn(column) && column.badge ? (
                        <Badge
                          variant={column.badge.variant}
                          size={column.badge.size ?? 'sm'}
                        >
                          {column.badge.text}
                        </Badge>
                      ) : (
                        <Badge
                          variant='primary'
                          size='sm'
                          className='invisible'
                        >
                          &nbsp;
                        </Badge>
                      )}
                      {isEmptyColumn(column) ? (
                        <EmptySlotHeader slot={column} />
                      ) : (
                        <ComparisonItemImage item={column} />
                      )}
                    </Flex>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
          </Table>
        </Flex>

        {/*
         * Sticky header — pins to top when scrolled.
         * Synced horizontally via headerScrollContainerRef.
         */}
        {/* Outer sticky wrapper holds both the scrolling header and the full-bleed divider */}
        <div
          className={cn(
            'sticky top-0 z-20',
            styleVersion === 'v1' &&
              'bg-white -mx-4 px-4 border-b border-neutral-200',
          )}
        >
          <Flex
            ref={headerScrollContainerRef}
            gap='none'
            className='bg-white overflow-x-scroll overscroll-x-none scrollbar-none'
          >
            <Table
              className='table-fixed w-full border-separate border-spacing-0'
              noWrapper
              style={tableStyle}
            >
              {colgroup}
              <TableHeader>
                {(() => {
                  const hasCta = columns.some(
                    (col) => !isEmptyColumn(col) && col.header.ctaTop,
                  );
                  return (
                    <>
                      <TableRow className='border-none'>
                        {columns.map((column, index) => (
                          <TableHead
                            key={`${column.id}-header-title`}
                            className={cn(
                              'px-2 pt-2 pb-0 align-top font-normal text-left',
                              hasPinned &&
                                index === 0 &&
                                'sticky left-0 z-10 bg-white',
                            )}
                          >
                            {isEmptyColumn(column) ? (
                              <Heading variant='h5' className='font-normal'>
                                {column.label}
                              </Heading>
                            ) : (
                              <Flex direction='column' gap='xxs'>
                                <Heading variant='h5'>
                                  {column.header.title}
                                </Heading>
                                {column.header.subtext && (
                                  <Text variant='b5' color='subtext' tag='span'>
                                    {column.header.subtext}
                                  </Text>
                                )}
                              </Flex>
                            )}
                          </TableHead>
                        ))}
                      </TableRow>
                      {hasCta && (
                        <TableRow className='border-none'>
                          {columns.map((column, index) => (
                            <TableHead
                              key={`${column.id}-header-cta`}
                              className={cn(
                                'px-2 pb-4 pt-2 align-top font-normal text-left',
                                hasPinned &&
                                  index === 0 &&
                                  'sticky left-0 z-10 bg-white',
                              )}
                            >
                              {!isEmptyColumn(column) &&
                                column.header.ctaTop && (
                                  <Button
                                    href={column.header.ctaTop.href}
                                    variant='primaryFill'
                                    size='sm'
                                    onClick={() => {
                                      column.header.ctaTop!.onClick?.();
                                      onCtaClick?.(
                                        column.id,
                                        column.header.ctaTop!,
                                        'top',
                                      );
                                      onProductClicked?.(column.id);
                                    }}
                                  >
                                    {column.header.ctaTop.label}
                                  </Button>
                                )}
                            </TableHead>
                          ))}
                        </TableRow>
                      )}
                    </>
                  );
                })()}
              </TableHeader>
            </Table>
          </Flex>
        </div>

        {/*
         * Body rows — horizontally scrollable.
         * Each rowDef renders as TWO <tr>s:
         *   1. Label row  (one <td> per column; first has label text + is sticky)
         *   2. Cells row  (one <td> per column, first is sticky when hasPinned)
         */}
        <Flex
          ref={scrollContainerRef}
          gap='none'
          className='scrollbar-none overflow-x-auto overscroll-x-none'
        >
          <Table
            aria-label='Comparison'
            className='table-fixed w-full border-separate border-spacing-0'
            noWrapper
            style={tableStyle}
          >
            {colgroup}
            <caption className='sr-only'>Comparison table</caption>
            <TableBody>
              {rowDefs.map((row) => (
                <React.Fragment key={row.key}>
                  {/* Label row — first cell has the row label and is sticky when pinned */}
                  <TableRow className='border-none'>
                    {columns.map((column, index) => (
                      <TableCell
                        key={`${column.id}-${row.key}-label`}
                        className={cn(
                          'py-2 align-top',
                          styleVersion === 'v1' && index === 0 && 'pr-3',
                          styleVersion === 'v1' &&
                            index > 0 &&
                            index < columns.length - 1 &&
                            'px-3',
                          styleVersion === 'v1' &&
                            index === columns.length - 1 &&
                            'pl-3',
                          styleVersion === 'v2' && 'px-3 bg-neutral-50',
                          hasPinned &&
                            index === 0 &&
                            styleVersion === 'v1' &&
                            'sticky left-0 z-10 bg-white',
                          hasPinned &&
                            index === 0 &&
                            styleVersion === 'v2' &&
                            'sticky left-0 z-10 bg-neutral-50',
                        )}
                      >
                        {index === 0 && (
                          <Text
                            variant='l1'
                            color='subtext'
                            weight={styleVersion === 'v1' ? 'regular' : 'bold'}
                          >
                            {row.label}
                          </Text>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                  {/* Cells row */}
                  <TableRow className='border-none'>
                    {columns.map((column, index) => (
                      <TableCell
                        key={column.id}
                        className={cn(
                          'pt-2 pb-sm align-top',
                          styleVersion === 'v1' && index === 0 && 'pr-3',
                          styleVersion === 'v1' &&
                            index > 0 &&
                            index < columns.length - 1 &&
                            'px-3',
                          styleVersion === 'v1' &&
                            index === columns.length - 1 &&
                            'pl-3',
                          styleVersion === 'v2' && 'px-2',
                          styleVersion === 'v1' && 'row-divider',
                          hasPinned &&
                            index === 0 &&
                            'sticky left-0 z-10 bg-white',
                        )}
                      >
                        {!isEmptyColumn(column) && (
                          <CellContent
                            row={row}
                            value={column.fields[row.key]}
                          />
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </React.Fragment>
              ))}

              {/* CTA bottom row */}
              <TableRow className='border-none'>
                {columns.map((column, index) => (
                  <TableCell
                    key={`${column.id}-cta`}
                    className={cn(
                      'py-4 align-top',
                      index === 0
                        ? 'pr-3'
                        : index === columns.length - 1
                          ? 'pl-3'
                          : 'px-3',
                      hasPinned && index === 0 && 'sticky left-0 z-10 bg-white',
                    )}
                  >
                    {!isEmptyColumn(column) && column.ctaBottom && (
                      <Button
                        href={column.ctaBottom.href}
                        variant='primaryFill'
                        size='sm'
                        className='w-fit'
                        onClick={() => {
                          column.ctaBottom!.onClick?.();
                          onCtaClick?.(column.id, column.ctaBottom!, 'bottom');
                          onProductClicked?.(column.id);
                        }}
                      >
                        {column.ctaBottom.label}
                      </Button>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableBody>
          </Table>
        </Flex>
      </Flex>

      {/* Pagination controls — outside the white card */}
      {safeItems.length > 2 && (
        <Flex
          direction='row'
          align='center'
          justify='center'
          gap='sm'
          role='group'
          aria-label='Comparison navigation'
        >
          <CarouselPrevious
            disabled={!canScrollPrev}
            onClick={scrollPrev}
            aria-label='Previous column'
            design='outline'
          />
          <CarouselDots
            count={totalPages}
            activeIndex={currentPage}
            onDotClick={scrollToPage}
            design='outline'
          />
          <CarouselNext
            disabled={!canScrollNext}
            onClick={scrollNext}
            aria-label='Next column'
            design='outline'
          />
        </Flex>
      )}
    </Flex>
  );
};

ComparisonTableMobile.displayName = 'ComparisonTableMobile';
